import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { API } from '../../lib/api';
import { toast } from 'sonner';
import {
  User, Phone, Mail, Calendar, Globe, FileText, Euro, Star,
  ChevronDown, ExternalLink, MessageSquare, Bell,
  Repeat, Crown, TrendingUp, Home, CheckCircle, Clock, 
  CreditCard, Plus, AlertTriangle, Eye, Gift, History,
  Award, Minus, Edit, Activity, X, ArrowLeft, Send
} from 'lucide-react';
import CommunicationTimeline from '../../components/CommunicationTimeline';

// Stat Card - Light theme
const StatCard = ({ label, value, icon: Icon, color = 'gray' }) => {
  const colorStyles = {
    gray: 'bg-gray-50 text-gray-900 border-gray-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    gold: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  
  const iconColors = {
    gray: 'text-gray-500',
    blue: 'text-blue-500',
    green: 'text-green-500',
    orange: 'text-orange-500',
    purple: 'text-purple-500',
    gold: 'text-amber-500',
  };
  
  return (
    <div className={`rounded-xl p-4 text-center border ${colorStyles[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className={`text-xs flex items-center justify-center gap-1 mt-1 ${iconColors[color]}`}>
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
    </div>
  );
};

// Smart Tag - Light theme
const SmartTag = ({ tag, color }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    green: 'bg-green-100 text-green-700 border-green-200',
    gold: 'bg-amber-100 text-amber-700 border-amber-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    orange: 'bg-orange-100 text-orange-700 border-orange-200',
  };
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${colors[color] || colors.blue}`}>
      {tag}
    </span>
  );
};

// Collapsible Section - Light theme
const Section = ({ title, icon: Icon, children, defaultOpen = true, count, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-500" />
          <span className="font-semibold text-gray-900">{title}</span>
          {count !== undefined && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
              {count}
            </span>
          )}
          {badge && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.color}`}>
              {badge.text}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="px-4 pb-4 border-t border-gray-100">{children}</div>}
    </div>
  );
};

// Timeline Event - Light theme
const TimelineEvent = ({ event }) => {
  const typeIcons = {
    booking_created: Calendar,
    checkin_completed: CheckCircle,
    payment_received: Euro,
    notification_push: Bell,
    notification_email: Mail,
    notification_whatsapp: MessageSquare,
    loyalty_add: Gift,
    loyalty_remove: Minus,
  };
  
  const typeColors = {
    booking_created: 'bg-blue-500',
    checkin_completed: 'bg-green-500',
    payment_received: 'bg-amber-500',
    notification_push: 'bg-purple-500',
    notification_email: 'bg-cyan-500',
    notification_whatsapp: 'bg-green-500',
    loyalty_add: 'bg-amber-500',
    loyalty_remove: 'bg-red-500',
  };
  
  const Icon = typeIcons[event.type] || Clock;
  
  return (
    <div className="flex gap-3 py-2">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${typeColors[event.type] || 'bg-gray-400'}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{event.title}</p>
        <p className="text-xs text-gray-500">{event.subtitle}</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {event.date ? new Date(event.date).toLocaleString('it-IT') : 'N/A'}
          {event.status === 'failed' && <span className="text-red-500 ml-2">Fallito</span>}
        </p>
      </div>
    </div>
  );
};

// Quick Action Button - Light theme
const QuickAction = ({ icon: Icon, label, onClick, variant = 'default' }) => {
  const variants = {
    default: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    success: 'bg-green-600 hover:bg-green-500 text-white',
    primary: 'bg-[#C5A059] hover:bg-[#B8934D] text-white',
    warning: 'bg-amber-500 hover:bg-amber-400 text-white',
  };
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${variants[variant]}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
};

// Guest Score Badge - Light theme
const GuestScore = ({ score }) => {
  const getColor = (value) => {
    if (value >= 85) return 'bg-green-500';
    if (value >= 70) return 'bg-blue-500';
    if (value >= 50) return 'bg-yellow-500';
    if (value >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  };
  
  return (
    <div className="flex items-center gap-3">
      <div className={`w-14 h-14 rounded-full ${getColor(score?.value || 0)} flex items-center justify-center shadow-lg`}>
        <span className="text-xl font-bold text-white">{score?.value || 0}</span>
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-900">{score?.label || 'N/A'}</p>
        <p className="text-xs text-gray-500">Guest Score</p>
      </div>
    </div>
  );
};

// AI Insight Card - Light theme
const AIInsight = ({ insight }) => {
  const icons = {
    repeat: Repeat,
    euro: Euro,
    globe: Globe,
    calendar: Calendar,
    'check-circle': CheckCircle,
    home: Home,
  };
  const Icon = icons[insight.icon] || Star;
  
  return (
    <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
      <Icon className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-gray-700">{insight.text}</p>
    </div>
  );
};

// Loyalty Points Modal - Light theme
const AddPointsModal = ({ isOpen, onClose, onSubmit, guestName, bookings = [], currentBalance = 0 }) => {
  const [operation, setOperation] = useState('add');
  const [points, setPoints] = useState('');
  const [importo, setImporto] = useState('');
  const [reason, setReason] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        operation,
        points: operation === 'correct' ? 0 : (parseInt(points) || 0),
        importo_spesa: parseFloat(importo) || null,
        reason: reason,
        booking_id: bookingId || null,
      };
      
      if (operation === 'correct') {
        payload.new_balance = parseInt(points) || 0;
      }
      
      await onSubmit(payload);
      onClose();
      setPoints('');
      setImporto('');
      setReason('');
      setBookingId('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Gestione Punti</h3>
              <p className="text-sm text-gray-500 mt-0.5">{guestName}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Saldo attuale: <span className="text-amber-600 font-bold">{currentBalance} pt</span>
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Operation Tabs */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => setOperation('add')}
              className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                operation === 'add' ? 'bg-green-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="loyalty-op-add"
            >
              <Plus className="w-4 h-4" />
              Aggiungi
            </button>
            <button
              type="button"
              onClick={() => setOperation('remove')}
              className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                operation === 'remove' ? 'bg-red-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="loyalty-op-remove"
            >
              <Minus className="w-4 h-4" />
              Rimuovi
            </button>
            <button
              type="button"
              onClick={() => setOperation('correct')}
              className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                operation === 'correct' ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="loyalty-op-correct"
            >
              <Edit className="w-4 h-4" />
              Correggi
            </button>
          </div>

          {operation !== 'correct' && (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Importo spesa (€)</label>
                <input
                  type="number"
                  value={importo}
                  onChange={(e) => {
                    setImporto(e.target.value);
                    setPoints(Math.floor(parseFloat(e.target.value || 0) / 10).toString());
                  }}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                  placeholder="Es: 150 (1pt ogni 10€)"
                  data-testid="loyalty-importo"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Oppure punti diretti</label>
                <input
                  type="number"
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                  placeholder="Es: 15"
                  data-testid="loyalty-points"
                />
              </div>
            </>
          )}

          {operation === 'correct' && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Nuovo saldo punti</label>
              <input
                type="number"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                placeholder="Es: 100"
                required
                data-testid="loyalty-new-balance"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Differenza: <span className={parseInt(points || 0) - currentBalance >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {parseInt(points || 0) - currentBalance > 0 ? '+' : ''}{parseInt(points || 0) - currentBalance} pt
                </span>
              </p>
            </div>
          )}

          {/* Booking Association */}
          {bookings?.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Associa a prenotazione</label>
              <select
                value={bookingId}
                onChange={(e) => setBookingId(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                data-testid="loyalty-booking"
              >
                <option value="">-- Nessuna prenotazione --</option>
                {bookings.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.code} - {b.arrival} ({b.unit})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Motivo / Note</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
              placeholder="Es: Soggiorno luglio 2026, bonus fedeltà..."
              rows={2}
              data-testid="loyalty-reason"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
              data-testid="loyalty-cancel"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 bg-[#C5A059] hover:bg-[#B8934D] rounded-lg text-white font-medium disabled:opacity-50 transition-colors shadow-sm"
              data-testid="loyalty-submit"
            >
              {submitting ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Loyalty History Modal - Light theme
const LoyaltyHistoryModal = ({ isOpen, onClose, transactions = [], guestName, loyaltyData }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Storico Punti</h3>
            <p className="text-sm text-gray-500 mt-0.5">{guestName}</p>
            <div className="flex gap-4 mt-2 text-sm">
              <span>Saldo: <span className="font-bold text-amber-600">{loyaltyData?.current_balance || 0} pt</span></span>
              <span>Guadagnati: <span className="font-bold text-green-600">{loyaltyData?.lifetime_earned || 0}</span></span>
              <span>Riscattati: <span className="font-bold text-purple-600">{loyaltyData?.lifetime_redeemed || 0}</span></span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {transactions.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <History className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500">Nessuna transazione registrata</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((t, i) => (
                <div 
                  key={t.id || i} 
                  className={`p-4 rounded-xl border ${
                    t.punti > 0 ? 'bg-green-50 border-green-200' : 
                    t.tipo === 'correzione' ? 'bg-blue-50 border-blue-200' :
                    'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        t.punti > 0 ? 'bg-green-500' : t.tipo === 'correzione' ? 'bg-blue-500' : 'bg-red-500'
                      }`}>
                        {t.punti > 0 ? <Plus className="w-5 h-5 text-white" /> : 
                         t.tipo === 'correzione' ? <Edit className="w-5 h-5 text-white" /> :
                         <Minus className="w-5 h-5 text-white" />}
                      </div>
                      <div>
                        <p className={`font-bold text-lg ${
                          t.punti > 0 ? 'text-green-700' : t.tipo === 'correzione' ? 'text-blue-700' : 'text-red-700'
                        }`}>
                          {t.punti > 0 ? '+' : ''}{t.punti} punti
                        </p>
                        <p className="text-xs text-gray-500 capitalize">{t.tipo}</p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      {t.created_at && new Date(t.created_at).toLocaleString('it-IT', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  </div>
                  
                  {/* Details */}
                  <div className="mt-3 pt-3 border-t border-gray-200/50 text-sm space-y-1">
                    {t.descrizione && (
                      <p className="text-gray-700">{t.descrizione}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                      {(t.old_balance !== undefined || t.new_balance !== undefined) && (
                        <span className="bg-white px-2 py-1 rounded">
                          Saldo: {t.old_balance ?? '?'} → {t.new_balance ?? '?'}
                        </span>
                      )}
                      {t.operator && (
                        <span className="bg-white px-2 py-1 rounded">Operatore: {t.operator}</span>
                      )}
                      {t.booking_id && (
                        <span className="bg-white px-2 py-1 rounded">Prenotazione: {t.booking_id}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Activity Item - Light theme
const ActivityItem = ({ activity, onAction }) => {
  const priorityStyles = {
    high: 'border-l-4 border-l-red-500 bg-red-50',
    normal: 'border-l-4 border-l-orange-500 bg-orange-50',
    low: 'border-l-4 border-l-blue-500 bg-blue-50',
  };

  return (
    <div className={`${priorityStyles[activity.priority]} p-3 rounded-r-lg`}>
      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
      <p className="text-xs text-gray-500">{activity.subtitle}</p>
      <button
        onClick={() => onAction(activity)}
        className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
      >
        Risolvi →
      </button>
    </div>
  );
};

export default function Guest360Page() {
  const { guestId } = useParams();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [showAddPoints, setShowAddPoints] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [loyaltyHistory, setLoyaltyHistory] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/crm/guests/${encodeURIComponent(guestId)}/360`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        toast.error('Ospite non trovato');
      }
    } catch (error) {
      console.error('Error fetching guest:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [token, guestId]);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/crm/guests/${encodeURIComponent(guestId)}/activities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setActivities(result.activities || []);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  }, [token, guestId]);

  const fetchLoyaltyHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/crm/guests/${encodeURIComponent(guestId)}/loyalty`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setLoyaltyHistory(result.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching loyalty history:', error);
    }
  }, [token, guestId]);

  useEffect(() => {
    if (token && guestId) {
      fetchData();
      fetchActivities();
    }
  }, [token, guestId, fetchData, fetchActivities]);

  // Action handlers
  const handleCall = () => {
    if (data?.profile?.phone) {
      window.open(`tel:${data.profile.phone}`);
    } else {
      toast.error('Numero non disponibile');
    }
  };

  const handleWhatsApp = () => {
    if (data?.profile?.phone) {
      const phone = data.profile.phone.replace(/\D/g, '');
      const msg = `Ciao ${data.profile.name?.split(' ')[0] || ''}, ti contattiamo da La Maisonette di Paestum.`;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    } else {
      toast.error('Numero non disponibile');
    }
  };

  const handleEmail = () => {
    if (data?.profile?.email) {
      window.open(`mailto:${data.profile.email}`);
    } else {
      toast.error('Email non disponibile');
    }
  };

  const handleAddPoints = async (pointsData) => {
    try {
      const response = await fetch(`${API}/admin/crm/guests/${encodeURIComponent(guestId)}/loyalty/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pointsData)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'aggiornamento punti');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const handleActivityAction = (activity) => {
    if (activity.booking_id) {
      window.location.href = `/admin/bookings?id=${activity.booking_id}`;
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Guest 360°">
        <div className="p-6 animate-pulse max-w-7xl mx-auto">
          <div className="h-32 bg-gray-200 rounded-xl mb-4"></div>
          <div className="h-64 bg-gray-200 rounded-xl"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout title="Guest 360°">
        <div className="p-6 text-center max-w-7xl mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Ospite non trovato</h2>
          <Link to="/admin/crm/guests" className="text-[#C5A059] hover:underline font-medium">
            ← Torna alla lista ospiti
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const { profile, stay_history, payment_history, loyalty, timeline, bookings, smart_tags, guest_score, ai_insights, documents, preferences } = data;

  return (
    <AdminLayout title="Guest 360°">
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Link to="/admin/crm/guests" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#C5A059] mb-2 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Guest CRM
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">{profile.name || 'Ospite'}</h1>
            <div className="flex flex-wrap gap-2 mt-2">
              {smart_tags?.map((tag, i) => (
                <SmartTag key={i} {...tag} />
              ))}
            </div>
          </div>
          <GuestScore score={guest_score} />
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <QuickAction icon={Phone} label="Chiama" onClick={handleCall} />
          <QuickAction icon={MessageSquare} label="WhatsApp" onClick={handleWhatsApp} variant="success" />
          <QuickAction icon={Mail} label="Email" onClick={handleEmail} />
          <QuickAction icon={Gift} label="Punti" onClick={() => setShowAddPoints(true)} variant="warning" />
          <Link to="/admin/bookings" className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-[#C5A059] hover:bg-[#B8934D] text-white transition-colors shadow-sm">
            <Plus className="w-4 h-4" />
            Nuova Prenotazione
          </Link>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile & Stats */}
          <div className="space-y-6">
            {/* Profile Card */}
            <Section title="Profilo" icon={User} defaultOpen={true}>
              <div className="space-y-3 pt-3">
                {profile.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-4 h-4 text-gray-500" />
                    </div>
                    <span className="text-gray-900 font-medium">{profile.phone}</span>
                  </div>
                )}
                {profile.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-4 h-4 text-gray-500" />
                    </div>
                    <span className="text-gray-900 font-medium">{profile.email}</span>
                  </div>
                )}
                {profile.nationality && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-4 h-4 text-gray-500" />
                    </div>
                    <span className="text-gray-900 font-medium">{profile.nationality}</span>
                  </div>
                )}
                {profile.birth_date && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-gray-500" />
                    </div>
                    <span className="text-gray-900 font-medium">{profile.birth_date}</span>
                  </div>
                )}
                {profile.document_type && (
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-4 h-4 text-gray-500" />
                    </div>
                    <span className="text-gray-900 font-medium">{profile.document_type} #{profile.document_number}</span>
                  </div>
                )}
              </div>
            </Section>

            {/* Stay Stats */}
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Soggiorni" value={stay_history.total_stays} icon={Home} color="blue" />
              <StatCard label="Notti totali" value={stay_history.total_nights} icon={Calendar} color="purple" />
              <StatCard label="Spesa totale" value={`€${stay_history.total_spent?.toFixed(0)}`} icon={Euro} color="green" />
              <StatCard label="Punti fedeltà" value={loyalty?.current_balance || 0} icon={Award} color="gold" />
            </div>

            {/* Loyalty Section */}
            <Section 
              title="Loyalty / Punti" 
              icon={Gift} 
              defaultOpen={true}
              badge={loyalty?.current_balance > 0 ? { text: `${loyalty.current_balance} pt`, color: 'bg-amber-100 text-amber-700' } : null}
            >
              <div className="space-y-4 pt-3">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                    <p className="text-xl font-bold text-amber-700" data-testid="loyalty-balance">{loyalty?.current_balance || 0}</p>
                    <p className="text-xs text-amber-600">Disponibili</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                    <p className="text-xl font-bold text-green-700" data-testid="loyalty-earned">{loyalty?.lifetime_earned || 0}</p>
                    <p className="text-xs text-green-600">Guadagnati</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                    <p className="text-xl font-bold text-purple-700" data-testid="loyalty-redeemed">{loyalty?.lifetime_redeemed || 0}</p>
                    <p className="text-xs text-purple-600">Riscattati</p>
                  </div>
                </div>
                
                {loyalty?.nights_available > 0 && (
                  <div className="text-center p-3 bg-amber-100 rounded-xl border border-amber-200">
                    <p className="text-sm text-amber-800 font-medium">
                      <Award className="w-4 h-4 inline mr-1" />
                      {loyalty.nights_available} notte{loyalty.nights_available > 1 ? 'i' : ''} gratis disponibil{loyalty.nights_available > 1 ? 'i' : 'e'}!
                    </p>
                  </div>
                )}

                {loyalty?.recent_transactions?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-2">Ultime transazioni</p>
                    {loyalty.recent_transactions.map((t, i) => (
                      <div key={i} className="flex justify-between text-sm py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <span className={`font-semibold ${t.punti > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {t.punti > 0 ? '+' : ''}{t.punti} pt
                          </span>
                          <span className="text-gray-400 ml-2 text-xs">{t.tipo}</span>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(t.created_at).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAddPoints(true)}
                    className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-400 rounded-lg text-white text-sm font-medium transition-colors shadow-sm"
                    data-testid="loyalty-manage-btn"
                  >
                    <Gift className="w-4 h-4 inline mr-1" />
                    Gestisci Punti
                  </button>
                  <button
                    onClick={() => {
                      fetchLoyaltyHistory();
                      setShowHistory(true);
                    }}
                    className="py-2.5 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium transition-colors"
                    data-testid="loyalty-history-btn"
                  >
                    <History className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Section>

            {/* AI Insights */}
            {ai_insights?.length > 0 && (
              <Section title="Insights" icon={Star} defaultOpen={false} count={ai_insights.length}>
                <div className="space-y-2 pt-3">
                  {ai_insights.map((insight, i) => (
                    <AIInsight key={i} insight={insight} />
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* Middle Column - Bookings & Payments */}
          <div className="space-y-6">
            {/* Activities (open tasks) */}
            {activities.length > 0 && (
              <Section title="Attività" icon={Activity} defaultOpen={true} count={activities.length}>
                <div className="space-y-2 pt-3">
                  {activities.slice(0, 5).map((activity, i) => (
                    <ActivityItem key={i} activity={activity} onAction={handleActivityAction} />
                  ))}
                </div>
              </Section>
            )}

            {/* Bookings */}
            <Section title="Prenotazioni" icon={Calendar} count={bookings?.length}>
              <div className="space-y-2 max-h-64 overflow-y-auto pt-3">
                {bookings?.map((booking, i) => (
                  <Link
                    key={i}
                    to={`/admin/bookings?id=${booking.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-100"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{booking.code}</p>
                      <p className="text-xs text-gray-500">{booking.unit} • {booking.arrival} → {booking.departure}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">€{booking.total?.toFixed(0)}</p>
                      <p className="text-xs text-gray-500">{booking.channel}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </Section>

            {/* Payment History */}
            <Section title="Pagamenti" icon={CreditCard} defaultOpen={true}>
              <div className="space-y-4 pt-3">
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                    <p className="text-xl font-bold text-green-700">€{payment_history?.total_paid?.toFixed(0) || 0}</p>
                    <p className="text-xs text-green-600">Pagato</p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-3 border border-orange-200">
                    <p className="text-xl font-bold text-orange-700">€{payment_history?.outstanding?.toFixed(0) || 0}</p>
                    <p className="text-xs text-orange-600">Da pagare</p>
                  </div>
                </div>
                
                {payment_history?.direct_payments?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-1">Pagamenti diretti</p>
                    {payment_history.direct_payments.slice(0, 3).map((p, i) => (
                      <div key={i} className="flex justify-between text-sm py-1.5">
                        <span className="text-gray-700">{p.booking_code}</span>
                        <span className={p.due > 0 ? 'text-orange-600 font-medium' : 'text-green-600 font-medium'}>
                          €{p.amount?.toFixed(0)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                {payment_history?.ota_payouts?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 font-medium mb-1">Accrediti OTA</p>
                    {payment_history.ota_payouts.slice(0, 3).map((p, i) => (
                      <div key={i} className="flex justify-between text-sm py-1.5">
                        <span className="text-gray-700">{p.booking_code} ({p.channel})</span>
                        <span className="text-blue-600 font-medium">€{p.amount?.toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Section>

            {/* Documents */}
            {documents?.length > 0 && (
              <Section title="Documenti" icon={FileText} count={documents.length} defaultOpen={false}>
                <div className="space-y-2 pt-3">
                  {documents.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.guest_name || doc.type}</p>
                        <p className="text-xs text-gray-500">{doc.type} {doc.number && `#${doc.number}`}</p>
                      </div>
                      {doc.url && (
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                          <Eye className="w-4 h-4 text-gray-500" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* Right Column - Communications & Timeline */}
          <div className="space-y-6">
            {/* Communications Section - NEW Phase 5.3B */}
            <Section title="Comunicazioni" icon={Send} defaultOpen={true} count={null}
              badge={{ text: 'Nuovo', color: 'bg-purple-100 text-purple-700' }}
            >
              <div className="pt-3">
                <CommunicationTimeline
                  guestId={guestId}
                  limit={10}
                  compact={true}
                  showQuickActions={true}
                />
              </div>
            </Section>

            <Section title="Timeline" icon={Clock} count={timeline?.length}>
              <div className="space-y-1 max-h-80 overflow-y-auto pt-3">
                {timeline?.length > 0 ? (
                  timeline.map((event, i) => (
                    <TimelineEvent key={i} event={event} />
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">Nessun evento</p>
                )}
              </div>
            </Section>

            {/* Stay History Details */}
            <Section title="Storico Soggiorni" icon={Home} defaultOpen={false}>
              <div className="space-y-3 text-sm pt-3">
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Primo soggiorno</span>
                  <span className="text-gray-900 font-medium">{stay_history.first_stay || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Ultimo soggiorno</span>
                  <span className="text-gray-900 font-medium">{stay_history.last_stay || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Durata media</span>
                  <span className="text-gray-900 font-medium">{stay_history.average_stay_length} notti</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Camera preferita</span>
                  <span className="text-gray-900 font-medium">{stay_history.favourite_room || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Stagione preferita</span>
                  <span className="text-gray-900 font-medium">{stay_history.favourite_season || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Cancellazioni</span>
                  <span className={stay_history.cancellations > 0 ? 'text-orange-600 font-medium' : 'text-green-600 font-medium'}>
                    {stay_history.cancellations}
                  </span>
                </div>
              </div>
            </Section>

            {/* Preferences */}
            <Section title="Preferenze" icon={Star} defaultOpen={false}>
              <div className="space-y-3 text-sm pt-3">
                {preferences?.preferred_channel && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-gray-500">Canale preferito</span>
                    <span className="text-gray-900 font-medium capitalize">{preferences.preferred_channel}</span>
                  </div>
                )}
                {preferences?.preferred_room && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-gray-500">Camera preferita</span>
                    <span className="text-gray-900 font-medium">{preferences.preferred_room}</span>
                  </div>
                )}
                {preferences?.preferred_season && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-gray-500">Stagione preferita</span>
                    <span className="text-gray-900 font-medium">{preferences.preferred_season}</span>
                  </div>
                )}
              </div>
            </Section>
          </div>
        </div>
      </div>

      {/* Add Points Modal */}
      <AddPointsModal
        isOpen={showAddPoints}
        onClose={() => setShowAddPoints(false)}
        onSubmit={handleAddPoints}
        guestName={profile.name}
        bookings={bookings}
        currentBalance={loyalty?.current_balance || 0}
      />

      {/* Loyalty History Modal */}
      <LoyaltyHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        transactions={loyaltyHistory}
        guestName={profile.name}
        loyaltyData={loyalty}
      />
    </AdminLayout>
  );
}
